import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtDate, fmtMoney } from "@/lib/api";
import { Buildings, Shield, FolderOpen } from "@phosphor-icons/react";

export default function PortalCases() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/portal/cases")
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Portal access denied"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-[12px] text-[var(--span-text-2)]" data-testid="portal-loading">Loading…</div>;
  if (err) {
    return (
      <div className="p-8" data-testid="portal-error">
        <div className="card-flat p-5 max-w-xl">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={18} weight="duotone" />
            <div className="font-display font-bold">Portal access required</div>
          </div>
          <div className="text-[12px] text-[var(--span-text-2)]">{err}</div>
          <div className="text-[11px] text-[var(--span-text-2)] mt-3">
            If you should have access, please contact your SPAN Operations point of contact to request a portal invite.
          </div>
        </div>
      </div>
    );
  }

  const { client, cases } = data;

  return (
    <div className="p-6 space-y-4" data-testid="portal-cases-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Buildings size={12}/> Client portal</div>
          <h1 className="font-display font-bold text-3xl mt-1">{client?.name || "Your cases"}</h1>
          <div className="text-[12px] text-[var(--span-text-2)] mt-1">
            {cases.length} active or recent {cases.length === 1 ? "case" : "cases"} under SPAN brand protection.
          </div>
        </div>
        <div className="text-[10px] font-mono text-[var(--span-text-2)]">{client?.country_code}</div>
      </div>

      <div className="card-flat p-0 overflow-hidden">
        <table className="w-full text-[12px]">
          <thead className="bg-[var(--span-surface-3)] text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Case</th>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Value (USD)</th>
              <th className="text-left px-3 py-2">Opened</th>
            </tr>
          </thead>
          <tbody>
            {cases.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[var(--span-text-2)]">No cases visible yet.</td></tr>
            )}
            {cases.map(c => (
              <tr key={c.case_id} className="border-t hairline" data-testid={`portal-case-row-${c.case_id}`}>
                <td className="px-3 py-2 font-mono text-[11px]">{c.case_code}</td>
                <td className="px-3 py-2">
                  <Link to={`/portal/cases/${c.case_id}`} className="text-[var(--span-accent)] hover:underline">
                    <FolderOpen size={11} weight="duotone" className="inline mr-1"/>{c.title}
                  </Link>
                </td>
                <td className="px-3 py-2">{c.country}</td>
                <td className="px-3 py-2">
                  <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-[var(--span-surface-3)]">{c.status}</span>
                </td>
                <td className="px-3 py-2 text-right font-mono tnum">{fmtMoney(c.value_usd, "USD")}</td>
                <td className="px-3 py-2 text-[var(--span-text-2)]">{fmtDate(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-[var(--span-text-2)]">
        Note: high-sensitivity PERS cases are excluded from external views per CEO policy.
      </div>
    </div>
  );
}
